/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bid, BidListResponse, ParticipantType } from '../../types';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import BackButton from '../common/BackButton';
import ReassignBidModal from './ReassignBidModal';
import BulkReassignModal from './BulkReassignModal';
import PaymentConfirmationModal from '../common/PaymentConfirmationModal';
import InsufficientCreditsModal from '../common/InsufficientCreditsModal';
import StripePaymentModal from '../common/StripePaymentModal';
import './BidList.css';

const BidList: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [bids, setBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<string>('');
  const [generatingBidId, setGeneratingBidId] = useState<number | null>(null);
  const [downloadingBidId, setDownloadingBidId] = useState<number | null>(null);
  const [reassigningBid, setReassigningBid] = useState<Bid | null>(null);
  const [selectedBidIds, setSelectedBidIds] = useState<Set<number>>(new Set());
  const [bulkReassigning, setBulkReassigning] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    name: '',
    author: '',
    dueDate: '',
    lastModified: '',
    archiveUntilDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('createdDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Payment-related state
  const [paymentConfirmation, setPaymentConfirmation] = useState<{
    bidId: number;
    costInCents: number;
    costFormatted: string;
    creditsFormatted: string;
    hasSufficientCredits: boolean;
  } | null>(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState<{
    bidId: number;
    costInCents: number;
    costFormatted: string;
    creditsFormatted: string;
  } | null>(null);
  const [showStripePayment, setShowStripePayment] = useState<{
    bidId: number;
    costInCents: number;
    costFormatted: string;
  } | null>(null);

  useEffect(() => {
    fetchBids();
  }, []);

  const fetchBids = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: BidListResponse = await apiClient.getAllBids();

      if (response.success && response.bids) {
        setBids(response.bids);
        setScope(response.scope || '');
      } else {
        setError(response.error || 'Failed to load bids');
      }
    } catch (err: any) {
      console.error('Error fetching bids:', err);
      setError(err.message || 'An error occurred while loading bids');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePackage = async (bidId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click navigation

    try {
      // Check credits before generating
      const creditCheck = await apiClient.checkCredits(bidId);

      if (!creditCheck.success || !creditCheck.creditCheck) {
        alert('Failed to check credits. Please try again.');
        return;
      }

      const { costInCents, costFormatted, creditsFormatted, hasSufficientCredits } = creditCheck.creditCheck;

      // If cost is 0, show simple confirmation
      if (costInCents === 0) {
        setPaymentConfirmation({
          bidId,
          costInCents,
          costFormatted,
          creditsFormatted,
          hasSufficientCredits: true
        });
        return;
      }

      // If has sufficient credits, show confirmation modal
      if (hasSufficientCredits) {
        setPaymentConfirmation({
          bidId,
          costInCents,
          costFormatted,
          creditsFormatted,
          hasSufficientCredits
        });
      } else {
        // Show insufficient credits modal
        setShowInsufficientCredits({
          bidId,
          costInCents,
          costFormatted,
          creditsFormatted
        });
      }
    } catch (err: any) {
      console.error('Error checking credits:', err);
      alert(`Error: ${err.message || 'An error occurred while checking credits'}`);
    }
  };

  const proceedWithGeneration = async (bidId: number) => {
    try {
      setGeneratingBidId(bidId);
      setPaymentConfirmation(null);
      await apiClient.generateBidPackage(bidId);
      // Download will start automatically via the API client
      // Refresh the bid list to update the hasGeneratedPdf status
      await fetchBids();
    } catch (err: any) {
      console.error('Error generating PDF package:', err);
      alert(`Error: ${err.message || 'An error occurred while generating the PDF package'}`);
    } finally {
      setGeneratingBidId(null);
    }
  };

  const handlePaymentConfirm = () => {
    if (paymentConfirmation) {
      proceedWithGeneration(paymentConfirmation.bidId);
    }
  };

  const handlePaymentCancel = () => {
    setPaymentConfirmation(null);
  };

  const handleInsufficientCreditsCancel = () => {
    setShowInsufficientCredits(null);
  };

  const handleContinueToPayment = () => {
    if (showInsufficientCredits) {
      setShowStripePayment({
        bidId: showInsufficientCredits.bidId,
        costInCents: showInsufficientCredits.costInCents,
        costFormatted: showInsufficientCredits.costFormatted
      });
      setShowInsufficientCredits(null);
    }
  };

  const handleStripePaymentSuccess = async (paymentIntentId: string) => {
    try {
      // Confirm payment and add credits
      const response = await apiClient.confirmPayment(paymentIntentId);

      if (response.success) {
        alert(`Payment successful! ${response.newCreditBalanceFormatted} added to your account.`);

        // Proceed with generation
        if (showStripePayment) {
          proceedWithGeneration(showStripePayment.bidId);
        }
        setShowStripePayment(null);
      } else {
        alert('Payment confirmation failed. Please contact support.');
        setShowStripePayment(null);
      }
    } catch (err: any) {
      console.error('Error confirming payment:', err);
      alert(`Error confirming payment: ${err.message}`);
      setShowStripePayment(null);
    }
  };

  const handleStripePaymentCancel = () => {
    setShowStripePayment(null);
  };

  const handleDownloadPdf = async (bidId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click navigation

    try {
      setDownloadingBidId(bidId);
      await apiClient.downloadBidPdf(bidId);
      // Download will start automatically via the API client
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      alert(`Error: ${err.message || 'An error occurred while downloading the PDF'}`);
    } finally {
      setDownloadingBidId(null);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const formatPrice = (price?: number): string => {
    if (price === undefined || price === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const formatAuthor = (author?: { firstName?: string; lastName?: string; username: string }): string => {
    if (!author) return '';
    if (author.firstName && author.lastName) {
      return `${author.firstName} ${author.lastName}`;
    }
    return author.username;
  };

  const handleReassignClick = (bid: Bid, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click navigation
    setReassigningBid(bid);
  };

  const handleReassignSuccess = () => {
    setReassigningBid(null);
    fetchBids(); // Refresh the bid list
  };

  const handleReassignClose = () => {
    setReassigningBid(null);
  };

  const handleFilterChange = (field: string, value: string) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearFilters = () => {
    setSearchFilters({
      name: '',
      author: '',
      dueDate: '',
      lastModified: '',
      archiveUntilDate: ''
    });
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with ascending as default
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // Filter bids based on search criteria
  const filteredBids = bids.filter(bid => {
    // Name filter (case insensitive)
    if (searchFilters.name && !bid.name.toLowerCase().includes(searchFilters.name.toLowerCase())) {
      return false;
    }

    // Author filter (case insensitive, searches firstName, lastName, and username)
    if (searchFilters.author && bid.author) {
      const authorSearch = searchFilters.author.toLowerCase();
      const firstName = bid.author.firstName?.toLowerCase() || '';
      const lastName = bid.author.lastName?.toLowerCase() || '';
      const username = bid.author.username?.toLowerCase() || '';

      if (!firstName.includes(authorSearch) &&
          !lastName.includes(authorSearch) &&
          !username.includes(authorSearch)) {
        return false;
      }
    }

    // Due date filter (matches YYYY-MM-DD format or partial)
    if (searchFilters.dueDate && bid.dueDate) {
      if (!bid.dueDate.includes(searchFilters.dueDate)) {
        return false;
      }
    }

    // Last modified filter (matches YYYY-MM-DD format or partial)
    if (searchFilters.lastModified && bid.lastModified) {
      if (!bid.lastModified.includes(searchFilters.lastModified)) {
        return false;
      }
    }

    // Archive until date filter (matches YYYY-MM-DD format or partial)
    if (searchFilters.archiveUntilDate && bid.archiveUntilDate) {
      if (!bid.archiveUntilDate.includes(searchFilters.archiveUntilDate)) {
        return false;
      }
    }

    return true;
  });

  // Sort bids based on selected column and direction
  const sortedBids = [...filteredBids].sort((a, b) => {
    let compareResult = 0;

    switch (sortColumn) {
      case 'name':
        compareResult = (a.name || '').localeCompare(b.name || '');
        break;
      case 'price':
        compareResult = (a.price || 0) - (b.price || 0);
        break;
      case 'dueDate':
        compareResult = (a.dueDate || '').localeCompare(b.dueDate || '');
        break;
      case 'createdDate':
        compareResult = (a.createdDate || '').localeCompare(b.createdDate || '');
        break;
      case 'bidBodyCount':
        compareResult = (a.bidBodyCount || 0) - (b.bidBodyCount || 0);
        break;
      case 'author':
        if (showAuthorColumn && a.author && b.author) {
          const aAuthor = formatAuthor(a.author);
          const bAuthor = formatAuthor(b.author);
          compareResult = aAuthor.localeCompare(bAuthor);
        }
        break;
      default:
        compareResult = 0;
    }

    return sortDirection === 'asc' ? compareResult : -compareResult;
  });

  // Check if any bid has author info (indicates admin/manager view)
  const showAuthorColumn = bids.some(bid => bid.author !== undefined);

  // Only managers and admins can reassign bids
  const canReassignBids = currentUser?.type === ParticipantType.MANAGER || currentUser?.type === ParticipantType.ADMIN;

  // Checkbox handling for bulk operations
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedBidIds(new Set(filteredBids.map(bid => bid.id)));
    } else {
      setSelectedBidIds(new Set());
    }
  };

  const handleSelectBid = (bidId: number) => {
    const newSelected = new Set(selectedBidIds);
    if (newSelected.has(bidId)) {
      newSelected.delete(bidId);
    } else {
      newSelected.add(bidId);
    }
    setSelectedBidIds(newSelected);
  };

  const handleBulkReassign = () => {
    setBulkReassigning(true);
  };

  const closeBulkReassign = () => {
    setBulkReassigning(false);
    setSelectedBidIds(new Set());
  };

  const handleBulkReassignSuccess = () => {
    setBulkReassigning(false);
    setSelectedBidIds(new Set());
    fetchBids();
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(searchFilters).some(value => value !== '');

  if (loading) {
    return (
      <div className="bid-list-container">
        <div className="loading">Loading bids...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bid-list-container">
        <div className="error-message">
          <h3>Error Loading Bids</h3>
          <p>{error}</p>
          <button onClick={fetchBids} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bid-list-container">
      <BackButton to="/" />
      <div className="bid-list-header">
        <div className="header-left">
          <h2>Bids</h2>
          {scope && <span className="scope-badge">{scope}</span>}
          <div className="bid-count">
            {hasActiveFilters ? (
              <>Showing {filteredBids.length} of {bids.length}</>
            ) : (
              <>Total: {bids.length}</>
            )}
          </div>
        </div>
        <div className="header-actions">
          <button
            className="toggle-filters-button"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          {canReassignBids && selectedBidIds.size > 0 && (
            <button
              className="bulk-reassign-button"
              onClick={handleBulkReassign}
            >
              Reassign Selected ({selectedBidIds.size})
            </button>
          )}
          {currentUser?.type === ParticipantType.USER && (
            <button
              className="create-bid-button"
              onClick={() => navigate('/bids/create')}
            >
              Create New Bid
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="search-filters">
          <div className="filter-header">
            <h3>Search & Filter</h3>
            {hasActiveFilters && (
              <button className="clear-filters-button" onClick={handleClearFilters}>
                Clear All Filters
              </button>
            )}
          </div>
          <div className="filter-grid">
            <div className="filter-field">
              <label htmlFor="filter-name">Bid Name</label>
              <input
                id="filter-name"
                type="text"
                placeholder="Search by name..."
                value={searchFilters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
              />
            </div>

            {showAuthorColumn && (
              <div className="filter-field">
                <label htmlFor="filter-author">Author</label>
                <input
                  id="filter-author"
                  type="text"
                  placeholder="Search by author name or username..."
                  value={searchFilters.author}
                  onChange={(e) => handleFilterChange('author', e.target.value)}
                />
              </div>
            )}

            <div className="filter-field">
              <label htmlFor="filter-due-date">Due Date</label>
              <input
                id="filter-due-date"
                type="date"
                value={searchFilters.dueDate}
                onChange={(e) => handleFilterChange('dueDate', e.target.value)}
              />
            </div>

            <div className="filter-field">
              <label htmlFor="filter-last-modified">Last Modified</label>
              <input
                id="filter-last-modified"
                type="date"
                value={searchFilters.lastModified}
                onChange={(e) => handleFilterChange('lastModified', e.target.value)}
              />
            </div>

            <div className="filter-field">
              <label htmlFor="filter-archive-until">Archive Until Date</label>
              <input
                id="filter-archive-until"
                type="date"
                value={searchFilters.archiveUntilDate}
                onChange={(e) => handleFilterChange('archiveUntilDate', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {bids.length === 0 ? (
        <div className="no-bids">
          <p>No bids found.</p>
        </div>
      ) : sortedBids.length === 0 ? (
        <div className="no-bids">
          <p>No bids match your search criteria.</p>
          <button onClick={handleClearFilters} className="retry-button">
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="bid-table">
            <thead>
              <tr>
                {canReassignBids && (
                  <th className="checkbox-column">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={selectedBidIds.size > 0 && selectedBidIds.size === sortedBids.length}
                    />
                  </th>
                )}
                <th className="sortable-header" onClick={() => handleSort('name')}>
                  Name{getSortIndicator('name')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('price')}>
                  Price{getSortIndicator('price')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('dueDate')}>
                  Due Date{getSortIndicator('dueDate')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('createdDate')}>
                  Created Date{getSortIndicator('createdDate')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('bidBodyCount')}>
                  Bid Bodies{getSortIndicator('bidBodyCount')}
                </th>
                {showAuthorColumn && (
                  <th className="sortable-header" onClick={() => handleSort('author')}>
                    Author{getSortIndicator('author')}
                  </th>
                )}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedBids.map((bid) => {
                // Determine navigation path based on user role
                const navigateTo = (currentUser?.type === ParticipantType.MANAGER ||
                                    currentUser?.type === ParticipantType.ADMIN ||
                                    currentUser?.type === ParticipantType.EXECUTIVE)
                  ? `/bids/${bid.id}/view`
                  : `/bids/${bid.id}/edit`;

                return (
                  <tr
                    key={bid.id}
                    onClick={() => navigate(navigateTo)}
                    className="clickable-row"
                  >
                  {canReassignBids && (
                    <td className="checkbox-column" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedBidIds.has(bid.id)}
                        onChange={() => handleSelectBid(bid.id)}
                      />
                    </td>
                  )}
                  <td className="bid-name">{bid.name}</td>
                  <td className="bid-price">{formatPrice(bid.price)}</td>
                  <td>{formatDate(bid.dueDate)}</td>
                  <td>{formatDate(bid.createdDate)}</td>
                  <td className="bid-body-count">{bid.bidBodyCount || 0}</td>
                  {showAuthorColumn && (
                    <td className="bid-author">{formatAuthor(bid.author)}</td>
                  )}
                  <td className="bid-actions">
                    <button
                      className="download-button"
                      onClick={(e) => handleDownloadPdf(bid.id!, e)}
                      disabled={!bid.hasGeneratedPdf || downloadingBidId === bid.id}
                      title={bid.hasGeneratedPdf ? "Download generated PDF package" : "No PDF available - generate one first"}
                    >
                      {downloadingBidId === bid.id ? 'Downloading...' : 'Download PDF'}
                    </button>
                    <button
                      className={bid.hasGeneratedPdf ? "update-package-button" : "generate-package-button"}
                      onClick={(e) => handleGeneratePackage(bid.id!, e)}
                      disabled={generatingBidId === bid.id}
                      title={bid.hasGeneratedPdf ? "Regenerate PDF package with latest changes" : "Generate comprehensive PDF package"}
                    >
                      {generatingBidId === bid.id ? 'Generating...' : (bid.hasGeneratedPdf ? 'Update PDF Package' : 'Generate PDF Package')}
                    </button>
                    <button
                      className="results-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/bids/${bid.id}/results`);
                      }}
                      title="View or edit bid results"
                    >
                      View Results
                    </button>
                    {canReassignBids && (
                      <button
                        className="reassign-button"
                        onClick={(e) => handleReassignClick(bid, e)}
                        title="Reassign this bid to another user"
                      >
                        Reassign
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {reassigningBid && (
        <ReassignBidModal
          bid={reassigningBid}
          onClose={handleReassignClose}
          onSuccess={handleReassignSuccess}
        />
      )}

      {bulkReassigning && (
        <BulkReassignModal
          bidIds={Array.from(selectedBidIds)}
          onClose={closeBulkReassign}
          onSuccess={handleBulkReassignSuccess}
        />
      )}

      {paymentConfirmation && (
        <PaymentConfirmationModal
          costFormatted={paymentConfirmation.costFormatted}
          creditsFormatted={paymentConfirmation.creditsFormatted}
          isFreeCost={paymentConfirmation.costInCents === 0}
          onConfirm={handlePaymentConfirm}
          onCancel={handlePaymentCancel}
        />
      )}

      {showInsufficientCredits && (
        <InsufficientCreditsModal
          costFormatted={showInsufficientCredits.costFormatted}
          creditsFormatted={showInsufficientCredits.creditsFormatted}
          onPayWithStripe={handleContinueToPayment}
          onCancel={handleInsufficientCreditsCancel}
        />
      )}

      {showStripePayment && (
        <StripePaymentModal
          costInCents={showStripePayment.costInCents}
          costFormatted={showStripePayment.costFormatted}
          onSuccess={handleStripePaymentSuccess}
          onCancel={handleStripePaymentCancel}
        />
      )}
    </div>
  );
};

export default BidList;
